import { Device } from "mediasoup-client";

const ICE_SERVERS_DEFAULT = [
  { urls: "stun:pluglr.com:5349" },
  {
    urls: "turn:pluglr.com:5349?transport=udp",
    username: "mypluggers",
    credential: "plug12keys345",
  },
  {
    urls: "turn:pluglr.com:5349?transport=tcp",
    username: "mypluggers",
    credential: "plug12keys345",
  },
];

const noop = () => {};
const STREAM_TYPE_RADIO_MIX = "radio-mix";
const STREAM_TYPE_RADIO_CALL = "radio-call";
const STREAM_TYPE_RADIO_COHOST = "radio-cohost";
const MEDIA_KIND_AUDIO = "audio";
const PRODUCER_POLL_INTERVAL_MS = 5000;

function ensureError(err) {
  if (!err) {
    return new Error("Unknown mediasoup error");
  }

  if (err instanceof Error) {
    return err;
  }

  if (typeof err === "string") {
    return new Error(err);
  }

  if (typeof err === "object") {
    const message =
      err.message ||
      err.msg ||
      err.error ||
      err.reason ||
      err.description ||
      err.details?.message ||
      err.data?.message ||
      err.statusText ||
      err.code ||
      err.status ||
      err.statusCode;

    let serialized = "";
    try {
      serialized = JSON.stringify(err);
    } catch (jsonErr) {
      serialized = String(err);
    }

    const normalized = new Error(
      message ||
        (serialized
          ? `Unknown mediasoup error ${serialized}`
          : "Unknown mediasoup error (empty server response)")
    );

    if (err.code || err.status || err.statusCode) {
      normalized.code = err.code || err.status || err.statusCode;
    }

    if (err.details) {
      normalized.details = err.details;
    }

    if (err.data) {
      normalized.data = err.data;
    } else if (!normalized.details) {
      normalized.data = err;
    }

    normalized.original = err;

    return normalized;
  }

  return new Error(String(err));
}

function emitWithAck(socket, event, payload = {}) {
  return new Promise((resolve, reject) => {
    if (!socket || typeof socket.emit !== "function") {
      reject(new Error("Socket not connected"));
      return;
    }
    socket.emit(event, payload, (error, response) => {
      if (error) {
        reject(ensureError(error));
        return;
      }
      if (response && response.error) {
        reject(ensureError(response.error));
        return;
      }
      resolve(response);
    });
  });
}

function normalizeProducer(candidate) {
  if (!candidate) return null;
  if (typeof candidate === "string") {
    return {
      producerId: candidate,
      kind: null,
      type: null,
      peerId: null,
      id: null,
    };
  }

  const producerId = candidate.producerId || candidate.id;
  if (!producerId) return null;

  return {
    producerId,
    kind: candidate.kind || candidate.producerKind || null,
    type: candidate.type || candidate.streamType || null,
    peerId: candidate.peerId || null,
    id: candidate.id || null,
    raw: candidate,
  };
}

export default class WebRTCListenerClient {
  constructor({
    socket,
    channelId,
    eventId,
    user,
    iceServers = ICE_SERVERS_DEFAULT,
    onTrack = noop,
    onStatusChange = noop,
    onError = noop,
    onCallStateChange = noop,
    onSpeaking = noop,
    onRoleChange = noop,
    useSocketIdForPeer = false,
  }) {
    this.socket = socket;
    this.channelId = channelId;
    this.eventId = eventId;
    this.user = user || {};
    this.iceServers = iceServers;
    this.onTrack = onTrack;
    this.onStatusChange = onStatusChange;
    this.onError = onError;
    this.onCallStateChange = onCallStateChange;
    this.onSpeaking = onSpeaking;
    this.onRoleChange = onRoleChange;
    this.useSocketIdForPeer = Boolean(useSocketIdForPeer);

    this.device = null;
    this.recvTransport = null;
    this.sendTransport = null;
    this.consumers = new Map();
    this.connected = false;
    this.initialProducerIds = [];
    this._refreshHandle = null;
    this.callProducer = null;
    this.micStream = null;
    this.micAudioContext = null;
    this.micSourceNode = null;
    this.audienceGainNode = null;
    this.monitorGainNode = null;
    this.audienceDestination = null;
    this.monitorDestination = null;
    this.monitorStream = null;
    this.processedMicTrack = null;
    this._audienceGain = 1;
    this._audienceMuted = false;
    this._selfMonitorGain = 0.5;
    this._selfMonitorEnabled = false;
    this._callActive = false;
    this.socketId = null;
    this.hostSocketId = null;
    this.audioInputDeviceId = null;
    this.serverRole = this.user?.role || null;

    this._registerSocketHandlers();
    try {
      this.onRoleChange(this._resolveRole());
    } catch (err) {
      console.warn("onRoleChange handler error", err);
    }
  }

  _resolveHostId() {
    if (this.useSocketIdForPeer) {
      return this.socketId || this.socket?.id || null;
    }

    if (this.user && typeof this.user === "object" && this.user.id) {
      return this.user.id;
    }

    return this.socketId || this.socket?.id || null;
  }

  _resolveListenerId() {
    if (this.useSocketIdForPeer) {
      return this.socketId || this.socket?.id || null;
    }

    if (this.user && typeof this.user === "object" && this.user.id) {
      return this.user.id;
    }

    return this._resolveHostId();
  }

  _resolveEmail() {
    if (
      this.user &&
      typeof this.user === "object" &&
      typeof this.user.email === "string"
    ) {
      return this.user.email;
    }

    return "";
  }

  _resolveRole() {
    if (this.serverRole) {
      return this.serverRole;
    }

    if (this.user && typeof this.user === "object" && this.user.role) {
      return this.user.role;
    }

    if (this.user && typeof this.user === "object" && this.user.id) {
      return "listener";
    }

    return "anon_listener";
  }

  _updateAssignedRole(role) {
    if (!role || typeof role !== "string") return;
    this.serverRole = role;
    if (this.user && typeof this.user === "object") {
      this.user = { ...this.user, role };
    }
    try {
      this.onRoleChange(this.serverRole);
    } catch (err) {
      console.warn("onRoleChange handler error", err);
    }
  }

  _hasCohostPrivileges() {
    return this._resolveRole() === "cohost";
  }

  _getDefaultOutboundStreamType() {
    return this._hasCohostPrivileges()
      ? STREAM_TYPE_RADIO_COHOST
      : STREAM_TYPE_RADIO_CALL;
  }

  async _waitForHostId(timeoutMs = 5000) {
    const immediate = this._resolveHostId();
    if (immediate) return immediate;

    if (!this.socket) {
      throw new Error("Socket not initialized");
    }

    return new Promise((resolve, reject) => {
      let settled = false;
      const finish = (id, err) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        this.socket.off("connect", handleConnect);
        if (err) {
          reject(err);
        } else {
          resolve(id);
        }
      };

      const handleConnect = () => {
        const id = this._resolveHostId();
        if (id) {
          finish(id);
        }
      };

      const timer = setTimeout(() => {
        finish(null, new Error("Timed out waiting for socket id"));
      }, timeoutMs);

      this.socket.once("connect", handleConnect);

      if (this.socket.connected && this.socket.id) {
        handleConnect();
      }
    });
  }

  _setStatus(message) {
    try {
      this.onStatusChange(message);
    } catch (err) {
      console.warn("WebRTCListenerClient status handler error", err);
    }
  }

  _reportError(error) {
    const normalized = error instanceof Error ? error : ensureError(error);
    try {
      this.onError(normalized);
    } catch (err) {
      console.warn("WebRTCListenerClient error handler error", err);
    }
  }

  setAudioInputDevice(deviceId) {
    const normalized = deviceId || null;
    if (this.audioInputDeviceId === normalized) {
      return;
    }
    this.audioInputDeviceId = normalized;
  }

  _registerSocketHandlers() {
    if (!this.socket) return;

    this.socket.on("disconnect", () => {
      this.connected = false;
      this._setStatus("Disconnected");
    });

    this.socket.on("connect", () => {
      this.connected = true;
      this.socketId = this.socket.id;
      this._setStatus("Connected");
    });

    this.socket.on(
      "promote-to-caller",
      async ({ callerId, roomId, direction }) => {
        try {
          await this._handlePromoteToCaller({ callerId, roomId, direction });
        } catch (err) {
          this._reportError(err);
        }
      }
    );

    this.socket.on("newProducer", async (payload) => {
      const normalized = normalizeProducer(payload);
      if (!normalized?.producerId) return;
      if (
        normalized.type &&
        normalized.type !== STREAM_TYPE_RADIO_MIX &&
        normalized.type !== MEDIA_KIND_AUDIO
      ) {
        return;
      }
      try {
        await this.consume(normalized.producerId);
      } catch (err) {
        this._reportError(err);
      }
    });

    this.socket.on("producerClosed", ({ producerId }) => {
      if (!producerId) return;
      for (const [consumerId, consumerData] of this.consumers.entries()) {
        if (consumerData.producerId === producerId) {
          try {
            consumerData.consumer.close();
          } catch (err) {
            console.warn("consumer close", err);
          }
          this.consumers.delete(consumerId);
        }
      }
    });
  }

  async prepare() {
    if (!this.socket) {
      throw new Error("Socket not connected");
    }

    const joinResponse = await this.joinRoom();
    this.initialProducerIds = this._extractProducerIds(joinResponse);
    await this._loadDevice();
    await this._ensureRecvTransport();
    await this._autoPublishCohostAudioIfNeeded();

    if (!this.initialProducerIds.length) {
      await this._refreshProducersOnce();
    }
  }

  async joinRoom() {
    const hostId = await this._waitForHostId().catch((err) => {
      throw ensureError(err);
    });
    const listenerId = this._resolveListenerId();
    const role = this._resolveRole();
    const payload = {
      roomId: this.eventId,
      isHost: false,
      // listenerId,
      hostId,
      // peerId: hostId,
      role,
      email: this._resolveEmail(),
      // channelId: this.channelId,
    };

    const response = await emitWithAck(this.socket, "joinRoom", payload);
    if (response && typeof response === "object") {
      this.hostSocketId =
        response.hostSocketId ||
        response.hostSocket ||
        response.hostId ||
        (response.host && response.host.socketId) ||
        null;
      if (response.role) {
        this._updateAssignedRole(response.role);
      }
    }
    this._setStatus("Joined room");
    return response;
  }

  _extractProducerIds(response) {
    if (!response) return [];
    const sources = [];

    if (Array.isArray(response)) sources.push(...response);
    if (Array.isArray(response?.producerIds))
      sources.push(...response.producerIds);
    if (Array.isArray(response?.producers)) sources.push(...response.producers);
    if (response?.producerId) sources.push(response.producerId);

    const normalized = sources
      .map((candidate) => normalizeProducer(candidate)?.producerId)
      .filter(Boolean);

    return [...new Set(normalized)];
  }

  async _loadDevice() {
    if (this.device) return;

    const { rtpCapabilities } = await emitWithAck(
      this.socket,
      "getRouterRtpCapabilities",
      { roomId: this.eventId }
    );

    if (!rtpCapabilities) {
      throw new Error("Router RTP capabilities missing");
    }

    const device = new Device();
    await device.load({ routerRtpCapabilities: rtpCapabilities });
    this.device = device;
    this._setStatus("Mediasoup device ready");
  }

  async _autoPublishCohostAudioIfNeeded() {
    if (!this._hasCohostPrivileges()) return;
    if (this.callProducer) return;
    const transport = await this._ensureSendTransport();
    await this._startCallMicrophone(transport, {
      streamType: STREAM_TYPE_RADIO_COHOST,
    });
  }

  async _ensureRecvTransport() {
    if (this.recvTransport) return;

    const transportResponse = await emitWithAck(
      this.socket,
      "createTransport",
      {
        forceTcp: false,
        producing: false,
        consuming: true,
      }
    );

    const params =
      transportResponse?.recvTransportParams ||
      transportResponse?.transportParams ||
      transportResponse;

    if (!params?.id) {
      throw new Error("Missing recv transport parameters");
    }

    const transport = this.device.createRecvTransport({
      ...params,
      iceServers: this.iceServers,
    });

    transport.on("connect", ({ dtlsParameters }, callback, errback) => {
      emitWithAck(this.socket, "connectTransport", {
        transportId: params.id,
        dtlsParameters,
      })
        .then(() => callback())
        .catch((err) => errback(err));
    });

    transport.on("connectionstatechange", (state) => {
      this._setStatus(`Recv transport: ${state}`);
      if (state === "failed") {
        this._reportError(new Error("Receive transport failed"));
      }
    });

    this.recvTransport = transport;
  }

  async start() {
    await this.prepare();

    if (Array.isArray(this.initialProducerIds)) {
      for (const producerId of this.initialProducerIds) {
        try {
          await this.consume(producerId);
        } catch (err) {
          this._reportError(err);
        }
      }
    }

    this._ensureProducerPoller();

    await emitWithAck(this.socket, "readyToConsume", {
      roomId: this.eventId,
      channelId: this.channelId,
    }).catch((err) => {
      console.warn("readyToConsume optional event failed", err);
    });
  }

  async consume(producerId) {
    if (!producerId) return null;
    await this._ensureRecvTransport();

    const consumeResponse = await emitWithAck(this.socket, "consume", {
      roomId: this.eventId,
      producerId,
      rtpCapabilities: this.device.rtpCapabilities,
      hostId: this._resolveHostId(),
      listenerId: this._resolveListenerId(),
      peerId: this._resolveHostId(),
    });

    const params = consumeResponse?.consumerParams || consumeResponse;
    const { id, kind, rtpParameters, type } = params || {};

    if (kind !== MEDIA_KIND_AUDIO) {
      await emitWithAck(this.socket, "resume", { consumerId: id }).catch(
        (err) => {
          console.warn("resume non-audio consumer failed", err);
        }
      );
      return null;
    }

    if (type && type !== STREAM_TYPE_RADIO_MIX) {
      await emitWithAck(this.socket, "resume", { consumerId: id }).catch(
        (err) => {
          console.warn("resume non-mix audio consumer failed", err);
        }
      );
      return null;
    }

    const consumer = await this.recvTransport.consume({
      id,
      producerId,
      kind,
      rtpParameters,
    });

    const stream = new MediaStream([consumer.track]);
    this.consumers.set(consumer.id, {
      consumer,
      producerId,
      type: type || null,
    });

    try {
      this.onTrack(stream);
    } catch (err) {
      console.warn("onTrack handler error", err);
    }

    await emitWithAck(this.socket, "resumeConsumer", {
      consumerId: consumer.id,
      producerId,
    }).catch((err) => {
      console.warn("resumeConsumer failed", err);
    });

    return consumer;
  }

  async _refreshProducersOnce() {
    try {
      const response = await emitWithAck(this.socket, "getProducers", {
        roomId: this.eventId,
      });

      const candidates = [];
      if (Array.isArray(response)) candidates.push(...response);
      if (Array.isArray(response?.producers))
        candidates.push(...response.producers);

      for (const candidate of candidates) {
        const normalized = normalizeProducer(candidate);
        if (!normalized?.producerId) continue;
        if (
          normalized.type &&
          normalized.type !== STREAM_TYPE_RADIO_MIX &&
          normalized.type !== MEDIA_KIND_AUDIO
        ) {
          continue;
        }

        try {
          await this.consume(normalized.producerId);
        } catch (err) {
          this._reportError(err);
        }
      }
    } catch (err) {
      console.warn("getProducers failed", err);
    }
  }

  _ensureProducerPoller() {
    if (this._refreshHandle) return;
    this._refreshHandle = setInterval(
      () => this._refreshProducersOnce(),
      PRODUCER_POLL_INTERVAL_MS
    );
  }

  setTrackMuted(muted) {
    for (const { consumer } of this.consumers.values()) {
      const track = consumer?.track;
      if (track) {
        track.enabled = !muted;
      }
    }
  }

  async cleanup() {
    this._closeCallProducer();

    if (this.sendTransport) {
      try {
        this.sendTransport.close();
      } catch (err) {
        console.warn("sendTransport close", err);
      }
      this.sendTransport = null;
    }

    if (this.micStream) {
      try {
        this.micStream.getTracks().forEach((track) => track.stop());
      } catch (err) {
        console.warn("mic stream stop", err);
      }
      this.micStream = null;
    }

    for (const { consumer } of this.consumers.values()) {
      try {
        consumer.close();
      } catch (err) {
        console.warn("consumer close", err);
      }
    }
    this.consumers.clear();

    if (this.recvTransport) {
      try {
        this.recvTransport.close();
      } catch (err) {
        console.warn("recvTransport close", err);
      }
      this.recvTransport = null;
    }
    this.device = null;
    this._setStatus("Listener cleaned up");
  }

  _teardownCallAudioPipeline(closeContext = true) {
    if (this.micSourceNode) {
      try {
        this.micSourceNode.disconnect();
      } catch (err) {
        console.warn("micSourceNode disconnect", err);
      }
      this.micSourceNode = null;
    }

    if (this.audienceGainNode) {
      try {
        this.audienceGainNode.disconnect();
      } catch (err) {
        console.warn("audienceGainNode disconnect", err);
      }
      this.audienceGainNode = null;
    }

    if (this.monitorGainNode) {
      try {
        this.monitorGainNode.disconnect();
      } catch (err) {
        console.warn("monitorGainNode disconnect", err);
      }
      this.monitorGainNode = null;
    }

    this.audienceDestination = null;
    this.monitorDestination = null;

    if (this.monitorStream) {
      try {
        this.monitorStream.getTracks().forEach((track) => track.stop());
      } catch (err) {
        console.warn("monitorStream stop", err);
      }
      this.monitorStream = null;
    }

    if (this.processedMicTrack) {
      try {
        this.processedMicTrack.stop();
      } catch (err) {
        console.warn("processedMicTrack stop", err);
      }
      this.processedMicTrack = null;
    }

    if (closeContext && this.micAudioContext) {
      try {
        this.micAudioContext.close();
      } catch (err) {
        console.warn("micAudioContext close", err);
      }
      this.micAudioContext = null;
    }
  }

  async _setupMicProcessing(stream) {
    this._teardownCallAudioPipeline(false);

    const AudioContextConstructor =
      typeof window !== "undefined" &&
      (window.AudioContext || window.webkitAudioContext);

    if (!AudioContextConstructor) {
      const [track] = stream.getAudioTracks();
      this.processedMicTrack = track || null;
      this.monitorStream = null;
      return;
    }

    if (!this.micAudioContext || this.micAudioContext.state === "closed") {
      this.micAudioContext = new AudioContextConstructor();
    }

    if (this.micAudioContext.state === "suspended") {
      try {
        await this.micAudioContext.resume();
      } catch (err) {
        console.warn("micAudioContext resume", err);
      }
    }

    const ctx = this.micAudioContext;
    const source = ctx.createMediaStreamSource(stream);

    const audienceGain = ctx.createGain();
    const monitorGain = ctx.createGain();
    const audienceDestination = ctx.createMediaStreamDestination();
    const monitorDestination = ctx.createMediaStreamDestination();

    // VAD Setup
    const analyser = this.micAudioContext.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.4;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    source.connect(analyser);
    this.vadAnalyser = analyser;

    // VAD Loop
    const VAD_THRESHOLD = 20; // Adjust based on testing (0-255)
    const SPEAKING_HOLD_MS = 500; // How long to hold "speaking" state after silence
    let lastSpeakingTime = 0;

    this.vadInterval = setInterval(() => {
      if (!this.vadAnalyser) return;

      this.vadAnalyser.getByteFrequencyData(dataArray);

      // Calculate average volume
      let sum = 0;
      for (let i = 0; i < bufferLength; i++) {
        sum += dataArray[i];
      }
      const average = sum / bufferLength;

      const now = Date.now();
      const currentlySpeaking = average > VAD_THRESHOLD;

      if (currentlySpeaking) {
        lastSpeakingTime = now;
      }

      const isSpeakingState =
        currentlySpeaking || now - lastSpeakingTime < SPEAKING_HOLD_MS;

      if (this.isSpeaking !== isSpeakingState) {
        this.isSpeaking = isSpeakingState;
        this.onSpeaking(isSpeakingState);
      }
    }, 100);

    this.micSourceNode = source;
    this.audienceGainNode = audienceGain;
    this.monitorGainNode = monitorGain;
    this.audienceDestination = audienceDestination;
    this.monitorDestination = monitorDestination;

    // Connect source to gain nodes (after VAD analyser)
    // The analyser is connected to the source, so it will process the audio before it goes to the gain nodes.
    // If we want VAD to analyze the *output* of the gain nodes, we'd connect it differently.
    // For typical VAD, analyzing the raw mic input is usually preferred.
    source.connect(audienceGain);
    audienceGain.connect(audienceDestination);

    source.connect(monitorGain);
    monitorGain.connect(monitorDestination);

    const [processedTrack] = audienceDestination.stream.getAudioTracks();
    this.processedMicTrack =
      processedTrack || stream.getAudioTracks()[0] || null;
    this.monitorStream = monitorDestination.stream;

    this._applyCallControlState();
  }

  _applyCallControlState() {
    this._updateAudienceGain();
    this._updateMonitorGain();
  }

  _updateAudienceGain() {
    if (this.audienceGainNode) {
      const gainValue = this._audienceMuted ? 0 : this._audienceGain;
      this.audienceGainNode.gain.value = gainValue;
    }
  }

  _updateMonitorGain() {
    if (this.monitorGainNode) {
      const gainValue = this._selfMonitorEnabled ? this._selfMonitorGain : 0;
      this.monitorGainNode.gain.value = gainValue;
    }

    if (this.monitorStream) {
      const [track] = this.monitorStream.getAudioTracks();
      if (track) {
        track.enabled = this._selfMonitorEnabled && this._selfMonitorGain > 0;
      }
    }
  }

  setAudienceVolume(volume) {
    const clamped = Number.isFinite(volume)
      ? Math.max(0, Math.min(1, volume))
      : 1;
    this._audienceGain = clamped;
    this._updateAudienceGain();
  }

  setAudienceMuted(muted) {
    const nextMuted = Boolean(muted);
    this._audienceMuted = nextMuted;
    this._updateAudienceGain();

    if (this.callProducer) {
      try {
        if (nextMuted) {
          this.callProducer.pause();
        } else {
          this.callProducer.resume();
        }
      } catch (err) {
        console.warn("callProducer pause/resume failed", err);
      }
    }
  }

  setSelfMonitorEnabled(enabled) {
    const nextEnabled = Boolean(enabled);
    this._selfMonitorEnabled = nextEnabled;

    if (
      this.micAudioContext &&
      this.micAudioContext.state === "suspended" &&
      nextEnabled
    ) {
      this.micAudioContext.resume().catch((err) => {
        console.warn("micAudioContext resume (monitor)", err);
      });
    }

    this._updateMonitorGain();
  }

  setSelfMonitorVolume(volume) {
    const clamped = Number.isFinite(volume)
      ? Math.max(0, Math.min(1, volume))
      : 0.5;
    this._selfMonitorGain = clamped;
    this._updateMonitorGain();
  }

  getMonitorStream() {
    return this.monitorStream || null;
  }

  isCallActive() {
    return this._callActive;
  }

  endCall() {
    this._closeCallProducer();
  }

  requestCall(name, targetSocketId = null) {
    if (!this.socket) {
      return Promise.reject(new Error("Socket not connected"));
    }

    const callerSocketId = this.socketId || this.socket?.id || undefined;

    const payload = {
      name: name || this.user?.name || "Listener",
      socketId: callerSocketId,
      targetSocketId: targetSocketId || this.hostSocketId || undefined,
    };

    this._notifyCallState({
      status: "requesting",
      message: "Sending call request",
    });

    return new Promise((resolve, reject) => {
      this.socket.emit("callRequest", payload, (error, response) => {
        if (error) {
          const err = ensureError(error);
          this._notifyCallState({ status: "error", message: err.message });
          reject(err);
          return;
        }
        if (response && response.error) {
          const err = ensureError(response.error);
          this._notifyCallState({ status: "error", message: err.message });
          reject(err);
          return;
        }
        this._notifyCallState({
          status: "requested",
          message: "Call request sent",
        });
        resolve(response);
      });
    });
  }

  async _handlePromoteToCaller({ callerId, roomId, direction }) {
    const allowedIds = new Set(
      [
        this.socketId,
        this.socket?.id || null,
        this._resolveListenerId(),
        this._resolveHostId(),
        this.user?.id || null,
      ].filter(Boolean)
    );

    if (callerId && !allowedIds.has(callerId)) {
      console.log(
        `${callerId} is not recognized as the active caller for room ${roomId}; continuing to listen.`
      );
      return;
    }

    this._notifyCallState({
      status: "promoted",
      message: "Promotion received, opening microphone",
    });

    if (direction) {
      await this._recreateTransport(direction).catch((err) => {
        throw err;
      });
    }

    const transport = await this._ensureSendTransport();
    await this._startCallMicrophone(transport);
  }

  // Ensure a send transport is created and is connected and ready to produce
  async _ensureSendTransport(force = false) {
    if (this.sendTransport && !force) {
      return this.sendTransport;
    }

    const transportResponse = await emitWithAck(
      this.socket,
      "createTransport",
      {
        forceTcp: false,
        producing: true,
        consuming: false,
        roomId: this.eventId,
        channelId: this.channelId,
      }
    );

    const params =
      transportResponse?.sendTransportParams ||
      transportResponse?.transportParams ||
      transportResponse;

    if (!params?.id) {
      throw new Error("Missing send transport parameters");
    }

    if (this.sendTransport) {
      try {
        this.sendTransport.close();
      } catch (err) {
        console.warn("Existing send transport close", err);
      }
    }

    const transport = this.device.createSendTransport({
      ...params,
      iceServers: this.iceServers,
    });

    this._wireSendTransport(transport, params.id);
    this.sendTransport = transport;
    return transport;
  }

  // Recreate a transport in the specified direction
  async _recreateTransport(direction) {
    if (direction === "send") {
      await this._ensureSendTransport(true);
    } else if (direction === "recv") {
      this.recvTransport = null;
      await this._ensureRecvTransport();
    }
  }

  _wireSendTransport(transport, serverTransportId) {
    transport.on("connect", ({ dtlsParameters }, callback, errback) => {
      emitWithAck(this.socket, "connectTransport", {
        transportId: serverTransportId,
        dtlsParameters,
        roomId: this.eventId,
        channelId: this.channelId,
      })
        .then(() => callback())
        .catch((err) => errback(err));
    });

    transport.on(
      "produce",
      ({ kind, rtpParameters, appData }, callback, errback) => {
        try {
          const hostId = this._resolveHostId();
          const listenerId = this._resolveListenerId();
          const role = this._resolveRole();
          const cohostUserId =
            (this.user && typeof this.user === "object" &&
              (this.user.id || this.user._id || this.user.userId)) ||
            null;
          const payloadAppData = {
            type: this._getDefaultOutboundStreamType(),
            callerId: listenerId,
            peerId: listenerId,
            callerSocketId: this.socket?.id || null,
            callerName: this.user?.name || this.user?.displayName || undefined,
            cohostUserId: cohostUserId || listenerId,
            cohostEmail:
              (this.user && typeof this.user.email === "string"
                ? this.user.email
                : null) || undefined,
            ...(appData || {}),
          };

          const producePayload = {
            transportId: serverTransportId,
            kind,
            appData: payloadAppData,
            rtpParameters,
            roomId: this.eventId,
            channelId: this.channelId,
            listenerId,
            hostId,
            peerId: listenerId,
            role,
          };

          console.log("produce()", producePayload);

          this.socket.emit("produce", producePayload, (error, response) => {
            if (error) {
              console.error("produce ack error raw", { error, response });
              const normalized = ensureError(error);
              console.error("Error producing:", normalized, {
                context: {
                  kind,
                  hasRtpParameters: Boolean(rtpParameters),
                  roomId: this.eventId,
                  channelId: this.channelId,
                },
              });
              try {
                errback(normalized);
              } catch (cbErr) {
                console.warn("produce errback failed", cbErr);
              }
              return;
            }

            const producerResponse =
              response && typeof response === "object"
                ? response
                : { id: response };
            const producerId =
              producerResponse?.id || producerResponse?.producerId;

            if (!producerId) {
              const err = new Error(
                "Invalid server response while producing listener microphone"
              );
              err.data = {
                response: producerResponse,
                request: producePayload,
              };
              console.error(err.message, err.data);
              try {
                errback(err);
              } catch (cbErr) {
                console.warn("produce invalid errback failed", cbErr);
              }
              return;
            }

            try {
              callback({ id: producerId });
            } catch (cbErr) {
              console.warn("produce callback failed", cbErr);
            }

            if (
              payloadAppData.type === STREAM_TYPE_RADIO_CALL ||
              payloadAppData.type === STREAM_TYPE_RADIO_COHOST
            ) {
              this._callActive = true;
              this._applyCallControlState();
              this._notifyCallState({
                status: "producing",
                message: "Microphone streaming",
              });
              this._refreshRadioMixAfterCall();
            }
          });
        } catch (err) {
          const normalized = ensureError(err);
          console.error("produceTransport handler error:", normalized);
          try {
            errback(normalized);
          } catch (cbErr) {
            console.warn("produce errback wrap failed", cbErr);
          }
        }
      }
    );
    transport.on("connectionstatechange", (state) => {
      this._setStatus(`Send transport: ${state}`);
      if (state === "failed") {
        this._notifyCallState({
          status: "error",
          message: "Send transport failed",
        });
      }
    });
  }

  async _startCallMicrophone(transport, { streamType } = {}) {
    if (!transport) {
      throw new Error("Send transport not available");
    }

    if (this.micStream) {
      try {
        this.micStream.getTracks().forEach((track) => track.stop());
      } catch (err) {
        console.warn("Stopping previous mic tracks", err);
      }
      this.micStream = null;
    }

    this._notifyCallState({
      status: "capturing",
      message: "Requesting microphone",
    });

    const audioConstraints = {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    };
    if (this.audioInputDeviceId) {
      audioConstraints.deviceId = { exact: this.audioInputDeviceId };
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: audioConstraints,
      video: false,
    });

    this.micStream = stream;
    const [track] = stream.getAudioTracks();
    if (!track) {
      throw new Error("Microphone track unavailable");
    }

    await this._setupMicProcessing(stream);
    const outboundTrack = this.processedMicTrack || track;

    const outboundType =
      streamType || this._getDefaultOutboundStreamType();

    this.callProducer = await transport.produce({
      track: outboundTrack,
      appData: { type: outboundType },
      codecOptions: {
        opusStereo: true,
        opusDtx: true,
        opusFec: true,
        opusPtime: 20,
      },
    });

    this.callProducer.on("transportclose", () => {
      this.callProducer = null;
      this._callActive = false;
      this._teardownCallAudioPipeline();
      this._notifyCallState({
        status: "ended",
        message: "Call transport closed",
      });
    });

    this.callProducer.on("trackended", () => {
      this._closeCallProducer();
    });

    this._notifyCallState({ status: "connected", message: "You are now live" });
    this._setStatus("Sharing microphone");
  }

  _closeCallProducer() {
    if (this.callProducer) {
      try {
        this.callProducer.close();
      } catch (err) {
        console.warn("callProducer close", err);
      }
      this.callProducer = null;
    }

    if (this.micStream) {
      try {
        this.micStream.getTracks().forEach((track) => track.stop());
      } catch (err) {
        console.warn("micStream stop", err);
      }
      this.micStream = null;
    }

    this._callActive = false;
    this._teardownCallAudioPipeline();
    this._audienceMuted = false;
    this._notifyCallState({ status: "ended", message: "Call ended" });
  }

  async _refreshRadioMixAfterCall() {
    let closedAny = false;
    for (const [consumerId, data] of this.consumers.entries()) {
      if (!data) continue;
      if (!data.type || data.type === STREAM_TYPE_RADIO_MIX) {
        try {
          data.consumer.close();
        } catch (err) {
          console.warn("radio mix consumer close", err);
        }
        this.consumers.delete(consumerId);
        closedAny = true;
      }
    }

    if (closedAny) {
      await this._refreshProducersOnce();
    } else {
      // Ensure we still refresh to retrieve potential updated mix
      await this._refreshProducersOnce();
    }
  }

  _notifyCallState(state) {
    try {
      this.onCallStateChange(state);
    } catch (err) {
      console.warn("call state handler error", err);
    }
  }
}
